import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Container,
  Group,
  Stack,
  Text,
} from '@mantine/core';
import { IconPencil, IconSparkles, IconTrash } from '@tabler/icons-react';
import PageLoader from 'src/UI/loader/PageLoader';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError } from 'src/error/ApiError';
import { useSiesta } from 'src/UI/app-container/SiestaProvider';
import { useAppContainer } from 'src/UI/app-container/useAppContainer';
import { formatLongDate } from 'src/utils/dateFormatting';
import { formatSkillName } from 'src/common/utils/skillNames';
import { useFlags } from 'src/feature-flags/FlagsHook';
import ShareSkillButton from './ShareSkillButton';
import UnshareSkillButton from './UnshareSkillButton';
import useTenantSkills from '../manage-siesta-skills/hooks/useTenantSkills';
import useDeleteTenantSkill from '../manage-siesta-skills/hooks/useDeleteTenantSkill';
import type { SiestaSkillListItem } from '../manage-siesta-skills/types';

const FIND_OUT_MORE_PROMPT =
  'What are custom skills and how can you use them to help me? Give me some examples.';

const UNSHARE_ON_DELETE_KEPT_COPIES = 'Firms that have already added it keep their own copy.';

const UNSHARE_ON_DELETE_DECLINED =
  'It will stay in the library, and you will not be able to unshare it after it is deleted.';

const CURATED_SKILL_EXAMPLES = [
  'Quarterly review pack',
  'Annual review letter',
  'Fact-find summary',
];

type CompanySkillsTabProps = {
  companyName: string | null;
  onBrowseCurated: () => void;
  onCreate: () => void;
  onEdit: (skillId: string) => void;
};

export default function CompanySkillsTab({
  companyName,
  onBrowseCurated,
  onCreate,
  onEdit,
}: CompanySkillsTabProps) {
  const [skillToDelete, setSkillToDelete] = useState<SiestaSkillListItem | null>(null);
  const [unshareOnDelete, setUnshareOnDelete] = useState(true);
  const { flagEnabledSkillLibraryPublishing } = useFlags();
  const canPublish = flagEnabledSkillLibraryPublishing === true;

  const skillsQuery = useTenantSkills();
  const { mutateAsync: deleteSkill, isPending: isDeleting } = useDeleteTenantSkill();

  const { setSiestaMode } = useAppContainer();
  const { sendUserMessage, resetIfDisplaced } = useSiesta();

  const skillToDeleteLabel = skillToDelete ? formatSkillName(skillToDelete.name) : '';
  const offersUnshare = canPublish && skillToDelete?.isShared === true;

  const askToDelete = (skill: SiestaSkillListItem) => {
    setUnshareOnDelete(true);
    setSkillToDelete(skill);
  };

  const handleAskSiesta = async () => {
    try {
      const conversationId = resetIfDisplaced();
      setSiestaMode('chat');
      await sendUserMessage(FIND_OUT_MORE_PROMPT, null, conversationId);
    } catch (error) {
      notifyAndLogError('Could not open Siesta', 'Please try again.', error);
    }
  };

  const handleConfirmDelete = async () => {
    if (!skillToDelete) return;
    try {
      await deleteSkill({ skillId: skillToDelete.id, unshare: offersUnshare && unshareOnDelete });
      setSkillToDelete(null);
    } catch (error) {
      const detail =
        error instanceof ApiError ? error.message : 'An error occurred while deleting the skill.';
      notifyAndLogError('Failed to delete skill', detail, error);
    }
  };

  return (
    <Container size="md" px={0}>
      <ConfirmDeleteModal
        opened={skillToDelete !== null}
        onClose={() => setSkillToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete skill"
        message={`Delete the '${skillToDeleteLabel}' skill? This cannot be undone.`}
        isLoading={isDeleting}
      >
        {offersUnshare && (
          <Checkbox
            mb="md"
            checked={unshareOnDelete}
            onChange={event => setUnshareOnDelete(event.currentTarget.checked)}
            label="Also unshare it from the skill library"
            description={
              unshareOnDelete ? UNSHARE_ON_DELETE_KEPT_COPIES : UNSHARE_ON_DELETE_DECLINED
            }
          />
        )}
      </ConfirmDeleteModal>
      <Stack gap="lg">
        <Stack gap="xs">
          <Text size="sm" c="dimmed" maw={560}>
            Skills {companyName ?? 'your firm'} has built or added. Everyone at the firm can use
            them in Siesta
            {canPublish
              ? ', and you can share any of them with other companies on Afternoon.'
              : '.'}
          </Text>
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconSparkles size={16} />}
            onClick={handleAskSiesta}
            styles={{ root: { paddingInline: 0 } }}
          >
            Ask Siesta to find out more
          </Button>
        </Stack>

        <SkillsList
          companyName={companyName}
          query={skillsQuery}
          onEdit={onEdit}
          onDelete={askToDelete}
          onBrowseCurated={onBrowseCurated}
          onCreate={onCreate}
        />
      </Stack>
    </Container>
  );
}

function SkillsList({
  companyName,
  query,
  onEdit,
  onDelete,
  onBrowseCurated,
  onCreate,
}: {
  companyName: string | null;
  query: ReturnType<typeof useTenantSkills>;
  onEdit: (skillId: string) => void;
  onDelete: (skill: SiestaSkillListItem) => void;
  onBrowseCurated: () => void;
  onCreate: () => void;
}) {
  const { data: skills, isLoading, isError } = query;

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  if (isError) {
    return (
      <Text c="red" role="alert">
        Failed to load skills. Please refresh the page or try again later.
      </Text>
    );
  }

  if (!skills?.length) {
    return (
      <Card withBorder padding="xl" radius="md">
        <Stack gap="lg" align="center">
          <Stack gap="xs" align="center">
            <Text fw={600} size="lg">
              {companyName ? `${companyName} has no skills yet` : 'You have no skills yet'}
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={470}>
              Most firms start with the curated skills, written and maintained by the Afternoon
              team. Add one and it becomes yours — edit the wording, turn it off, or delete it
              whenever you like.
            </Text>
          </Stack>
          <Stack gap="xs" w="100%" maw={520}>
            {CURATED_SKILL_EXAMPLES.map(title => (
              <Card key={title} withBorder padding="xs" radius="md" bg="gray.0">
                <Group gap="sm" wrap="nowrap">
                  <IconSparkles size={16} color="var(--mantine-color-orange-8)" aria-hidden />
                  <Text size="sm" fw={600}>
                    {title}
                  </Text>
                </Group>
              </Card>
            ))}
          </Stack>
          <Group gap="md">
            <Button onClick={onBrowseCurated}>Browse curated skills</Button>
            <Button variant="subtle" onClick={onCreate}>
              Or create your own
            </Button>
          </Group>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="sm">
      {skills.map(skill => (
        <SkillRow key={skill.id} skill={skill} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </Stack>
  );
}

function SkillRow({
  skill,
  onEdit,
  onDelete,
}: {
  skill: SiestaSkillListItem;
  onEdit: (skillId: string) => void;
  onDelete: (skill: SiestaSkillListItem) => void;
}) {
  const { flagEnabledSkillLibraryPublishing } = useFlags();
  const isDisabled = !skill.isEnabled;
  const metaLine = buildSkillMetaLine(skill);
  const label = formatSkillName(skill.name);

  const canPublish = flagEnabledSkillLibraryPublishing === true;
  const canUnshare = canPublish && skill.isShared;

  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      bg={isDisabled ? 'gray.0' : undefined}
      style={skill.isShared ? { borderLeft: '3px solid var(--mantine-color-orange-6)' } : undefined}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={6} style={{ minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text fw={600} c={isDisabled ? 'gray.9' : undefined}>
              {label}
            </Text>
            {skill.isShared && (
              <Badge size="xs" color="orange" variant="filled">
                Shared
              </Badge>
            )}
            {isDisabled && (
              <Badge size="xs" color="gray" variant="light">
                Disabled
              </Badge>
            )}
          </Group>
          <Text size="sm" c={isDisabled ? 'gray.7' : 'dimmed'}>
            {skill.description}
          </Text>
          {metaLine && (
            <Text size="xs" c="gray.7">
              {metaLine}
            </Text>
          )}
        </Stack>
        <Group gap="xs" wrap="nowrap">
          {canPublish && (!skill.isShared || skill.hasUnpublishedChanges) && (
            <ShareSkillButton skillId={skill.id} skillLabel={label} isShared={skill.isShared} />
          )}
          {canUnshare && <UnshareSkillButton skillId={skill.id} skillLabel={label} />}
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={`Edit ${label}`}
            onClick={() => onEdit(skill.id)}
          >
            <IconPencil size={16} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="red"
            aria-label={`Delete ${label}`}
            onClick={() => onDelete(skill)}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Group>
    </Card>
  );
}

function buildSkillMetaLine(skill: SiestaSkillListItem): string | null {
  const formattedDate = skill.updatedAt ? formatLongDate(skill.updatedAt) : null;
  if (!skill.isEnabled) {
    return formattedDate ? `Hidden from Siesta · updated ${formattedDate}` : 'Hidden from Siesta';
  }
  return formattedDate ? `Updated ${formattedDate}` : null;
}
